import { createApi } from '@reduxjs/toolkit/query/react';
import { request, ClientError } from 'graphql-request';
import config from 'src/config';
import authService from 'src/services/auth-service';
import { JSONValue } from 'src/shared/types';

const graphApiBaseQuery =
    ({ baseUrl }: { baseUrl: string }) =>
    async ({ body, variables = {} }: { body: string; variables?: JSONValue }) => {
        try {
            const accessToken = await authService.getAccessToken();

            if (!accessToken) {
                console.info('GraphQL request failed: No access token available');
                return {
                    error: {
                        status: 401,
                        data: 'No access token available',
                        message: 'Authentication required',
                    },
                };
            }

            const headers = {
                authorization: `${accessToken}`,
            };

            const result = await request(baseUrl, body, variables, headers);
            return { data: result };
        } catch (error) {
            if (error instanceof ClientError) {
                // Handle 401 specifically - don't log as error since it's expected user flow
                if (error.response.status === 401) {
                    console.info('GraphQL 401 error - token may be expired, signing out user');
                    // Clear any cached auth data and redirect to login
                    authService.signout();

                    return {
                        error: {
                            status: 401,
                            data: error.response.data,
                            response: {
                                errors: error.response.errors,
                            },
                            message: 'Authentication expired. Please log in again.',
                        },
                    };
                }

                // Log other GraphQL errors as they may be actual issues
                console.error('GraphQL error', {
                    status: error.response.status,
                    message: error.message,
                    errors: error.response.errors,
                });

                return {
                    error: {
                        status: error.response.status,
                        data: error.response.data,
                        response: {
                            errors: error.response.errors,
                        },
                        message: error.message,
                    },
                };
            }

            // Handle network errors
            if (error instanceof Error && error.name === 'AbortError') {
                console.info('GraphQL request was aborted');
                return {
                    error: {
                        status: 'FETCH_ERROR',
                        error: 'Request was aborted',
                    },
                };
            }

            // Only log unexpected errors
            console.error('Unexpected GraphQL error:', error);
            return {
                error: {
                    status: 500,
                    meta: { error: (error as Error).message },
                    message: 'An unexpected error occurred',
                },
            };
        }
    };

export default createApi({
    reducerPath: 'graphApi',
    baseQuery: graphApiBaseQuery({
        baseUrl: config.graphApiUrl,
    }),
    endpoints: () => ({}), // will inject endpoints in other files
    tagTypes: ['customer', 'project', 'user', 'users', 'customerAdmin', 'sampling-event'],
});
